import type {
  StorageUnitUpdate,
  UpdateActionOrigin,
  AccessCreateRequest,
  AccessDelete,
  StorageUnitStatus,
  StorageUnitCreate,
  ProjectMetadata,
  WorkflowMetadata,
  Lease,
} from './types';
import {
  StorageUnitType,
  GET_STORAGE_UNIT_REQUEST,
  UPDATE_STORAGE_UNIT_REQUEST,
  GET_STORAGE_UNITS_REQUEST,
  REMOVE_STORAGE_UNIT_REQUEST,
  GRANT_ACCESS_REQUEST,
  GET_ACCESS_INFORMATION_FOR_STORAGE_UNIT_REQUEST,
  LEASE_ON_STORAGE_UNIT_REQUEST,
  LeaseAction,
  LeaseType,
  LEASE_ON_STORAGE_UNIT_SUCCESS,
  POLL_STORAGE_UNIT_LEASES_REQUEST,
  REMOVE_ACCESS_REQUEST,
  CREATE_STORAGE_UNIT_REQUEST,
  GET_TEMPLATES_REQUEST,
  GET_APPROVED_FILES_REQUEST,
  GET_RECENT_STORAGE_UNITS_REQUEST,
  GET_ARCHIVED_REQUEST,
  ARCHIVE_STORAGE_UNIT,
  RESTORE_STORAGE_UNIT,
  DELETE_ALL_ARCHIVED_STORAGE_UNIT,
} from './types';
import { addFileEndingIfMissing } from 'util/file';
import dayjs from 'dayjs';
import type { NavigateFunction } from 'react-router-dom';

type CreateStorageUnitOptions = {
  isUpload?: boolean;
  openAfterCreate?: boolean;
  navigate?: NavigateFunction;
  projectMetadata?: ProjectMetadata;
  flowsheetMetadata?: WorkflowMetadata;
  undoable?: boolean;
};

export interface CreateStorageUnitRequestActionInterface {
  [extraProps: string]: unknown;
  type: typeof CREATE_STORAGE_UNIT_REQUEST;
  payload: {
    tempId: string;
    newStorageUnit: StorageUnitCreate;
    isUpload?: boolean;
    openAfterCreate?: boolean;
    navigate?: NavigateFunction;
    undoable?: boolean;
  };
}

export function createStorageUnitRequest(
  name: string,
  parentId?: string,
  fileData?: string,
  status?: StorageUnitStatus[],
  options: CreateStorageUnitOptions = {},
): CreateStorageUnitRequestActionInterface {
  const type = fileData ? StorageUnitType.File : StorageUnitType.Folder;

  return {
    type: CREATE_STORAGE_UNIT_REQUEST,
    payload: {
      tempId: 'tempId-' + dayjs().valueOf(),
      newStorageUnit: {
        name: fileData ? addFileEndingIfMissing(name) : name,
        parentId,
        type,
        fileData,
        status,
        projectMetadata: options.projectMetadata,
        flowsheetMetadata: options.flowsheetMetadata,
      },
      isUpload: options.isUpload,
      openAfterCreate: options.openAfterCreate,
      navigate: options.navigate,
      undoable: options.undoable,
    },
  };
}

export interface GetStorageUnitsRequestActionInterface {
  [extraProps: string]: unknown;
  type: typeof GET_STORAGE_UNITS_REQUEST;
  payload: {
    parentId?: string;
    descendantId?: string;
    searchString?: string;
    limit?: number;
    offset?: number;
    replace?: boolean;
    type?: StorageUnitType;
  };
}

export interface GetRecentStorageUnitRequestActionInterface {
  [extraProps: string]: unknown;
  type: typeof GET_RECENT_STORAGE_UNITS_REQUEST;
}

export function getRecentRequest(): GetRecentStorageUnitRequestActionInterface {
  return {
    type: GET_RECENT_STORAGE_UNITS_REQUEST,
  };
}

export function getStorageUnitsRequest({
  limit,
  offset,
  parentId,
  descendantId,
  searchString,
  type,
  replace = true,
}: GetStorageUnitsRequestActionInterface['payload']): GetStorageUnitsRequestActionInterface {
  return {
    type: GET_STORAGE_UNITS_REQUEST,
    payload: {
      limit,
      offset,
      parentId,
      descendantId,
      searchString,
      type,
      replace,
    },
  };
}

export interface GetTemplatesRequestActionInterface {
  [extraProps: string]: unknown;
  type: typeof GET_TEMPLATES_REQUEST;
  payload: {
    limit: number;
    offset: number;
    searchString?: string;
    replace?: boolean;
  };
}

export function getTemplatesRequest({
  limit,
  offset,
  searchString,
  replace = true,
}: GetTemplatesRequestActionInterface['payload']): GetTemplatesRequestActionInterface {
  return {
    type: GET_TEMPLATES_REQUEST,
    payload: {
      limit,
      offset,
      replace,
      searchString,
    },
  };
}

export interface GetArchivedRequestActionInterface {
  [extraProps: string]: unknown;
  type: typeof GET_ARCHIVED_REQUEST;
  payload: {
    limit: number;
    offset: number;
    replace?: boolean;
  };
}

export function getArchivedRequest({
  limit,
  offset,
  replace = true,
}: GetArchivedRequestActionInterface['payload']): GetArchivedRequestActionInterface {
  return {
    type: GET_ARCHIVED_REQUEST,
    payload: {
      limit,
      offset,
      replace,
    },
  };
}

export interface GetApprovedFilesRequestActionInterface {
  [extraProps: string]: unknown;
  type: typeof GET_APPROVED_FILES_REQUEST;
  payload: {
    limit: number;
    offset: number;
    searchString?: string;
    replace?: boolean;
  };
}

export function getApprovedRequest({
  limit,
  offset,
  searchString,
  replace = true,
}: GetApprovedFilesRequestActionInterface['payload']): GetApprovedFilesRequestActionInterface {
  return {
    type: GET_APPROVED_FILES_REQUEST,
    payload: {
      limit,
      offset,
      searchString,
      replace,
    },
  };
}

export interface GetStorageUnitRequestActionInterface {
  [extraProps: string]: unknown;
  type: typeof GET_STORAGE_UNIT_REQUEST;
  payload: {
    id: string;
  };
}

export function getStorageUnitRequest(
  id: string,
): GetStorageUnitRequestActionInterface {
  return {
    type: GET_STORAGE_UNIT_REQUEST,
    payload: {
      id,
    },
  };
}

export interface UpdateStorageUnitRequestActionInterface {
  [extraProps: string]: unknown;
  type: typeof UPDATE_STORAGE_UNIT_REQUEST;
  payload: {
    storageUnit: StorageUnitUpdate;
    origin: UpdateActionOrigin;
  };
}

export function updateStorageUnitRequest(
  storageUnit: StorageUnitUpdate,
  origin: UpdateActionOrigin,
): UpdateStorageUnitRequestActionInterface {
  const { type, name } = storageUnit;
  if (type === StorageUnitType.File && name) {
    storageUnit.name = addFileEndingIfMissing(name);
  }
  return {
    type: UPDATE_STORAGE_UNIT_REQUEST,
    payload: {
      storageUnit,
      origin,
    },
  };
}

export interface RemoveStorageUnitRequestActionInterface {
  [extraProps: string]: unknown;
  type: typeof REMOVE_STORAGE_UNIT_REQUEST;
  payload: {
    id: string;
  };
}

export function removeStorageUnitRequest(
  id: string,
): RemoveStorageUnitRequestActionInterface {
  return {
    type: REMOVE_STORAGE_UNIT_REQUEST,
    payload: {
      id,
    },
  };
}

export interface ArchiveStorageUnitRequestActionInterface {
  [extraProps: string]: unknown;
  type: typeof ARCHIVE_STORAGE_UNIT;
  payload: {
    id: string;
  };
}

export function archiveStorageUnitRequest(
  id: string,
): ArchiveStorageUnitRequestActionInterface {
  return {
    type: ARCHIVE_STORAGE_UNIT,
    payload: {
      id,
    },
  };
}

export interface DeleteAllArchivedStorageUnitRequestActionInterface {
  [extraProps: string]: unknown;
  type: typeof DELETE_ALL_ARCHIVED_STORAGE_UNIT;
  payload: Record<string, unknown>;
}

export function deleteAllArchivedStorageUnitRequest(): DeleteAllArchivedStorageUnitRequestActionInterface {
  return {
    type: DELETE_ALL_ARCHIVED_STORAGE_UNIT,
    payload: {},
  };
}

export interface RestoreStorageUnitRequestActionInterface {
  [extraProps: string]: unknown;
  type: typeof RESTORE_STORAGE_UNIT;
  payload: {
    id: string;
  };
}

export function restoreStorageUnitRequest(
  id: string,
): RestoreStorageUnitRequestActionInterface {
  return {
    type: RESTORE_STORAGE_UNIT,
    payload: {
      id,
    },
  };
}

export interface LeaseOnStorageUnitRequestActionInterface {
  [extraProps: string]: unknown;
  type: typeof LEASE_ON_STORAGE_UNIT_REQUEST;
  payload: {
    id: string;
    type: LeaseAction;
    leaseType?: LeaseType;
  };
}

export function leaseOnStorageUnitRequest(
  id: string,
  type: LeaseAction,
  leaseType?: LeaseType,
): LeaseOnStorageUnitRequestActionInterface {
  const effectiveLeaseType =
    type === LeaseAction.Acquire ? (leaseType ?? LeaseType.Soft) : leaseType;

  return {
    type: LEASE_ON_STORAGE_UNIT_REQUEST,
    payload: {
      id,
      type,
      leaseType: effectiveLeaseType,
    },
  };
}

export interface PollStorageUnitLeasesRequestActionInterface {
  [extraProps: string]: unknown;
  type: typeof POLL_STORAGE_UNIT_LEASES_REQUEST;
  payload: {
    id: string;
  };
}

export function pollStorageUnitLeasesRequest(
  id: string,
): PollStorageUnitLeasesRequestActionInterface {
  return {
    type: POLL_STORAGE_UNIT_LEASES_REQUEST,
    payload: {
      id,
    },
  };
}

export interface LeaseOnStorageUnitSuccessActionInterface {
  [extraProps: string]: unknown;
  type: typeof LEASE_ON_STORAGE_UNIT_SUCCESS;
  payload: {
    id: string;
    type: LeaseAction;
    leaseType?: LeaseType;
    lease?: Lease;
    userId?: string;
  };
}

export function leaseOnStorageUnitSuccess(
  id: string,
  type: LeaseAction,
  leaseType?: LeaseType,
  lease?: Lease,
  userId?: string,
): LeaseOnStorageUnitSuccessActionInterface {
  return {
    type: LEASE_ON_STORAGE_UNIT_SUCCESS,
    payload: {
      id,
      type,
      leaseType,
      lease,
      userId: userId ?? lease?.userId,
    },
  };
}

export interface GetAccessInformationForStorageUnitRequestActionInterface {
  [extraProps: string]: unknown;
  type: typeof GET_ACCESS_INFORMATION_FOR_STORAGE_UNIT_REQUEST;
  payload: {
    storageUnitId: string;
    openedThroughMembers: boolean;
  };
}

export function getAccessInformationForStorageUnitRequest(
  storageUnitId: string,
  openedThroughMembers = false,
): GetAccessInformationForStorageUnitRequestActionInterface {
  return {
    type: GET_ACCESS_INFORMATION_FOR_STORAGE_UNIT_REQUEST,
    payload: {
      storageUnitId,
      openedThroughMembers,
    },
  };
}

export interface GrantAccessToStorageUnitRequestActionInterface {
  [extraProps: string]: unknown;
  type: typeof GRANT_ACCESS_REQUEST;
  payload: {
    storageUnitId: string;
    access: AccessCreateRequest;
    openedThroughMembers: boolean;
  };
}

export function grantAccessToStorageUnit(
  storageUnitId: string,
  access: AccessCreateRequest,
  openedThroughMembers = false,
): GrantAccessToStorageUnitRequestActionInterface {
  return {
    type: GRANT_ACCESS_REQUEST,
    payload: {
      storageUnitId,
      access,
      openedThroughMembers,
    },
  };
}

export interface RemoveAccessToStorageUnitRequestActionInterface {
  [extraProps: string]: unknown;
  type: typeof REMOVE_ACCESS_REQUEST;
  payload: {
    id: string;
    access: AccessDelete;
    openedThroughMembers: boolean;
  };
}

export function removeAccessToStorageUnitRequest(
  id: string,
  access: AccessDelete,
  openedThroughMembers = false,
): RemoveAccessToStorageUnitRequestActionInterface {
  return {
    type: REMOVE_ACCESS_REQUEST,
    payload: {
      id,
      access,
      openedThroughMembers,
    },
  };
}
